import { useEffect, useState, useSyncExternalStore } from 'react'
import { Icon } from '../ui/Icon'
import { PixelPet } from '../identity/PixelPet'
import { LegalSheet } from '../legal/LegalSheet'
import { DOCS, gapCount, type Doc } from '../legal/documents'
import { pixelAvatarsOn, setPixelAvatars, subscribePixelAvatars } from '../identity/avatarPref'
import { AccountSection, SignOutSection } from '../auth/AccountSection'
import { DeleteAccountButton } from '../auth/DeleteAccount'
import { VoiceSection } from '../billing/VoiceSection'
import { billingUiOn } from '../billing/flags'
import { MachinesSection } from './MachinesScreen'
import { ReportSection } from './ReportSection'
import { FriendsSection } from './FriendsScreen'
import { getShellBuild } from './shellOrigin'
import { ErrorBoundary } from './ErrorBoundary'

/**
 * The MAIN MENU: everything about the ACCOUNT — who you are signed in as, the
 * machines paired to it, and how to add another.
 *
 * It opened life as a panel about "this server" and carried that machine's name
 * as its title. It is not about a machine: the one this browser happens to relay
 * through is just a row under MACHINES like any other, and naming the screen
 * after it made the account settings look like a property of an edge (owner,
 * 2026-08-10: aowl 是 edge 機器名稱, 跟 app 設定頁面無關). The name went, and the
 * /config query that existed only to fetch it went with it.
 *
 * The file keeps its name so the diff stays readable; the export is what App
 * renders in place of the agent/project list.
 */
export function ServerPanel({
  onClose,
}: {
  onClose: () => void
}) {

  /*
   * THE MAIN MENU IS A SIDEBAR MODE — owner, 2026-08-09: main menu 應該要有
   * 自己的版面。而且啟動後應該要自動跳出取代 side panel 下方的 Agent /
   * project list. (After a first attempt swapped the MAIN stage instead — 用
   * Agent 的版面當底放置 main menu? 什麼神邏輯.)
   *
   * So: this renders IN PLACE OF <Sidebar/> — the agent/project list — inside
   * the rail, below the host row. The stage (terminal / clean view / project)
   * is untouched. On a phone the rail is the root screen, so the menu covers
   * the screen there with no extra rule. Its own layout, its own room to grow.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <nav id="serverMenu" aria-label="Main menu">
      {/* No title — owner, 2026-08-10: 移除這個 aowl 的頁面 title. aowl 是 edge
          機器名稱, 跟 app 設定頁面無關. This menu is about the ACCOUNT (who you
          are, which machines are paired); the machine this browser happens to
          relay through is a different subject, and it already appears as a row
          under MACHINES. The brand above the rail names the screen. */}
      <div className="svm-hd">
        <button type="button" className="sheet-x" onClick={onClose} aria-label="Close menu">
          <Icon name="close" size={16} label="Close menu" />
        </button>
      </div>
      <div className="svm-body">
      {/* First, because it answers "who am I here" — the question every other setting
          on this panel is scoped by. It replaced the vault username, which was a name
          this app asked you to invent rather than one anything else would recognise. */}
      {/* EACH PART IN ITS OWN CARD — owner, 2026-09-30: 不同的元件你應該要用不同的視覺, 不同的
          卡片 … Friends 跟 Machine 就要用不同的卡片框起來. 使用者可以 sign out 的地方應該也是
          另外一個部分. */}
      <section className="svm-card">
        <AccountSection />
      </section>
      {/* The plan, right under who you are: which tier, what is left of today's voice
          allowance, when it resets, and the one button that changes it — owner,
          2026-09-09 (billing/VoiceSection.tsx). */}
      {/* Only in a build that says so (billing/flags.ts) — owner, 2026-09-10: the
          subscription has not passed its formal test yet, so prod ships without it. */}
      {billingUiOn() && (
        <section className="svm-card">
        <ErrorBoundary
          fallback={(reset, err) => (
            <>
              <div className="gh-sec">Advanced Feature</div>
              <p className="gh-note mch-err">Couldn’t read the plan — {err.message}</p>
              <button type="button" className="mch-btn" onClick={reset}>
                Retry
              </button>
            </>
          )}
        >
          <VoiceSection />
        </ErrorBoundary>
        </section>
      )}
      {/*
        * Server name and Change password are gone, and they left for the same reason:
        * both named things that no longer exist here.
        *
        * The name belonged to an edge server this app was the frontend for. There is
        * no edge now — pairing has not been designed — so the field would have been
        * renaming nothing, and a control that appears to work while acting on nothing
        * is worse than an absent one.
        *
        * The password was the vault's, and the vault is replaced by Google. Leaving
        * the form would have offered a way to change a credential that no longer
        * authenticates anybody, which is the more dangerous half: it looks like
        * security maintenance.
        */}
      {/*
        * "Open on another device" is gone, and with it the QR and the copyable link.
        *
        *   owner, 2026-08-05: 移除 — written on a screenshot with a box drawn around
        *   this exact section.
        *
        * What it handed out was `https://<machine>:8022/?t=<token>`, and this panel
        * said so in red: the link signs the device in, treat it like a password.
        * That was honest, and it was the previous topology's whole shape — a machine
        * with a port, reachable by anyone holding a string.
        *
        * Here the browser never reaches a machine. It signs in with Google, the
        * account holds the device binding, and the router carries every request down
        * a link the machine itself opened. There is no address to scan, and that is
        * the point: what this control distributed is the thing the new architecture
        * exists in order not to have.
        *
        * `/qr` still exists on machines that serve a port. Nothing here asks for it.
        */}

      {/*
        * GROUP is gone from this panel, and the red `vault_locked` under it with it.
        *
        *   owner, 2026-08-05: 「Frontend 的這個 Vault Locked 可以移除了」
        *
        * It was not a display bug. Groups are encrypted under the vault, and this
        * topology no longer creates one — measured on the owner's machine:
        * `/security/status` answers `configured: false, needs_setup: true`. So the
        * section could only ever render its own refusal, in the server's raw error
        * code, on a screen where the vault is not a concept the person has met.
        *
        * The component stays in the repo. Groups are a real feature of the previous
        * topology and deleting it would lose what it did; what is removed is the
        * claim that it works here.
        */}

      {/*
        * OTHER SERVERS and DISPLAY are gone from this panel.
        *
        *   owner, 2026-08-05: a box drawn around both sections, 「都可以拿掉了」
        *
        * OTHER SERVERS mounted remotes over SSH — one machine reaching another and
        * proxying it. This topology does not do that: other machines are paired
        * DEVICES, each holding its own link to the router, and the account reaches
        * them through it. The list could only say "can't reach it" about machines
        * that are, in fact, reachable — by a route this panel knows nothing about.
        *
        * DISPLAY was a viewport probe: gap, frame, visual, keyboard. It exists for
        * the on-screen keyboard work in the native shells and it is the right tool
        * for that; it is not a setting, and a panel of numbers a person cannot act
        * on is furniture on the one screen they came to for an answer.
        *
        * Both components stay in the repo. Neither is wrong — they answer questions
        * this screen has stopped asking.
        */}

      {/*
        * Where "Open on another device" used to be, and answering the question that
        * one actually answered: how do I get another machine into this account.
        * The old one handed out an address and a token; this one hands out an install
        * command, because in this topology the machine comes to us.
        */}
      {/*
        * Pairing, next to installing, because they are one errand: you install the edge
        * on a machine, run `avengents pair`, and scan what it prints. Splitting them
        * across two screens is how the native scanner ended up somewhere nobody could
        * reach.
        */}
      {/* "Add another machine" (InstallSection — the install commands + the inline
          pair scanner) MOVED to the + button's Agent tab, under the machine picker —
          owner, 2026-08-16: pairing 搬到 + button create agent 的 tab 下. Pairing a
          machine and choosing one to start on are one errand; this panel is now the
          account roster + machine list. */}

      {/* Its own boundary: a hostile device shape must degrade to a line here,
          never take down the terminal above it. */}
      <section className="svm-card">
      <ErrorBoundary
        fallback={(reset, err) => (
          <>
            <div className="gh-sec">Machines</div>
            <p className="gh-note mch-err">Couldn’t draw the machines list — {err.message}</p>
            <button type="button" className="mch-btn" onClick={reset}>
              Retry
            </button>
          </>
        )}
      >
        <MachinesSection />
      </ErrorBoundary>
      </section>

      {/* Friends live here, beside the machines — owner, 2026-08-19: 解除好友放到主選單,
          好友選單裡置入解除風險太高 (誤觸), 解除要雙重確認. Removal is account management,
          reached deliberately, not brushed past mid-conversation. */}
      <section className="svm-card">
      <ErrorBoundary
        fallback={(reset, err) => (
          <>
            <div className="gh-sec">Friends</div>
            <p className="gh-note mch-err">Couldn’t draw the friends list — {err.message}</p>
            <button type="button" className="mch-btn" onClick={reset}>
              Retry
            </button>
          </>
        )}
      >
        <FriendsSection />
      </ErrorBoundary>
      </section>

      {/* Appearance — placed in the LIVE menu flow. The first draft sat inside a
          ViewportDiagnostics panel that production never rendered (its strings were
          absent from the built bundle) — a switch in a room that does not exist. That
          panel was deleted on 2026-09-02: shell/ViewportProbe.tsx runs the identical
          effect and IS rendered, behind ?vp. */}
      <section className="svm-card">
        <div className="gh-sec">Display</div>
        <PixelAvatarRow />
      </section>

      <section className="svm-card">
        <ShellBuildRow />
        <ReportSection />
      </section>

      {/* NOT BEHIND billingUiOn(), AND THAT IS A REQUIREMENT RATHER THAN A TASTE.
          App Review guideline 5.1.1(i) wants the privacy policy reachable INSIDE the
          app "in an easily accessible manner", for every app — not for every app that
          happens to have its paywall switched on. A build shipped with billing off
          would have had no privacy policy in it at all, and that is a rejection.

          It DID ride that switch, for a reason that was sound and has since expired:
          "a prod build without the subscription does not show documents still marked
          ⟦to be filled in⟧". The documents were drafts then. They were finished on
          2026-09-10 — gapCount is 0 for both and documents.test.ts fails if a gap
          ever returns — so the condition now guards against a state that cannot
          happen, while hiding something we are obliged to show. */}
      {/* ABOVE Legal, and not behind any switch — guideline 1.2 requires a way to
          report content for every build that carries user content, the same way the
          privacy policy below it is required for every build. App Review returned 1.0
          under 2.1 asking to see this and the blocking mechanism; blocking is Humans →
          Remove, and this is the half that did not exist. */}
      <section className="svm-card">
        <LegalSection />
      </section>

      {/* Sign out MOVED UP on 2026-09-13, to the right of the account row it acts on
          (owner: 放置於使用者顯示頭像的那一個欄位的右邊) — AccountSection renders it now.
          What stays here is the way out for good — guideline 5.1.1(v): deletion must
          START INSIDE THE APP, an email to support does not count. Quiet here,
          explicit in its sheet (auth/DeleteAccount.tsx). */}
      <section className="svm-card svm-card-out">
        <SignOutSection />
        <DeleteAccountButton />
      </section>
      </div>
    </nav>
  )
}

/**
 * The privacy policy and the terms, reachable from the menu.
 *
 * IN THE APP, not only in App Store Connect: guideline 5.1.1(i) requires the privacy
 * policy "within the app in an easily accessible manner", and Schedule 2 of the PLA
 * requires the terms to be linkable from the screen that asks for a subscription.
 * The bottom of the main menu is where people look for both, and it is one tap from
 * anywhere.
 *
 * The count of unwritten passages is shown on the row itself rather than only inside
 * the document, so nobody can ship believing these are done without having been told
 * on the way past (owner, 2026-09-09: 先將隱私條款與政策的草稿放在主設定畫面裡面 …
 * 以後草稿內容我們可以再修改).
 */
function LegalSection() {
  const [open, setOpen] = useState<Doc | null>(null)
  return (
    <>
      <div className="gh-sec">Legal</div>
      {DOCS.map((doc) => {
        const gaps = gapCount(doc)
        return (
          <div className="sp-row" key={doc.id}>
            <span className="sp-host-name">
              {doc.title}
              {gaps > 0 && <em> draft · {gaps} to write</em>}
            </span>
            <button type="button" className="gh-mini" onClick={() => setOpen(doc)}>
              Read
            </button>
          </div>
        )
      })}
      {open && <LegalSheet doc={open} onClose={() => setOpen(null)} />}
    </>
  )
}

/**
 * Which build of the native shell this device is running — read from the stamp
 * the shell puts in its User-Agent (AvengentsShell/<sha>, +dirty when the tree was
 * not clean; shellOrigin.getShellBuild).
 *
 * The reader existed for a month with nothing to read: the shell wrote its
 * `appendUserAgent` under `server`, where Capacitor ignores it, and its own check
 * read the CONFIG and said so ("on the device UNKNOWN") — both ends green, the
 * middle severed (avg-app-builder, 2026-09-09, fixed at frontend 79c51ae). Now
 * that the stamp arrives, this is where the owner, testing a build on a real
 * phone, reads which build it is. Nothing in a browser: no shell, no row.
 */
function ShellBuildRow() {
  const build = getShellBuild()
  if (!build) return null
  return (
    <>
      <div className="gh-sec">This app</div>
      <p className="gh-note">
        Shell build <code>{build}</code>
        {build.endsWith('+dirty') ? ' — built from an uncommitted tree' : ''}
      </p>
    </>
  )
}

/**
 * The pixel-avatar switch — owner, 2026-08-26: 主選單…Enable / Disable
 * Avengents Pixel Avatar; Disable 時所有 PixelPet 頭像變成簡單的小圓圈.
 *
 * The preview IS a live PixelPet, so the row demonstrates the current answer
 * instead of describing it: flip the switch and the face beside the label
 * becomes the dot every avatar in the app just became. Per-browser, like the
 * theme — see identity/avatarPref.ts for why this is not account state.
 */
function PixelAvatarRow() {
  const on = useSyncExternalStore(subscribePixelAvatars, pixelAvatarsOn, pixelAvatarsOn)
  return (
    <div className="sp-row">
      <PixelPet name="avatar preview" seed="menu-preview" size={22} />
      <span className="sp-host-name">
        Pixel avatars
        {!on && <em> simple dots</em>}
      </span>
      <button
        type="button"
        className="gh-mini"
        aria-pressed={on}
        onClick={() => setPixelAvatars(!on)}
      >
        {on ? 'On' : 'Off'}
      </button>
    </div>
  )
}
